import React, { useState } from 'react';
import { getClientId, getSessionCreationDate } from '../../utils/client_id';
import {
  AppBar,
  Box,
  Container,
  IconButton,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  Chip,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Avatar,
} from '@mui/material';
import { 
  Settings as SettingsIcon, 
  DataObject as DataObjectIcon,
  ArrowBack as ArrowBackIcon,
  AccountCircle as AccountCircleIcon,
  ExitToApp as LogoutIcon
} from '@mui/icons-material';
import { DataSummary } from '../../types/api';
import { useAuth } from '../../context/AuthContext';

interface AppLayoutProps {
  children: React.ReactNode;
  onConfigOpen: () => void;
  hasData: boolean;
  dataSummary?: DataSummary;
  onBackToStart?: () => void;
}

const AppLayout: React.FC<AppLayoutProps> = ({ 
  children, 
  onConfigOpen, 
  hasData, 
  dataSummary,
  onBackToStart
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  
  const { username, logout } = useAuth();
  const clientId = getClientId();
  const sessionCreationDate = getSessionCreationDate();
  
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };
  
  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };
  
  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static" color="primary" elevation={1}>
        <Toolbar>
          <DataObjectIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            ABTSolute Concierge
          </Typography>
          
          {hasData && onBackToStart && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={onBackToStart}
              startIcon={<ArrowBackIcon />}
              size="small"
              sx={{ 
                mr: 2, 
                borderColor: 'rgba(255, 255, 255, 0.5)',
                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                py: 0.5,
                display: { xs: 'none', sm: 'flex' }
              }}
            >
              Back to Start
            </Button>
          )}
          
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setSessionDialogOpen(true)}
            size="small"
            sx={{ 
              mr: 2, 
              borderColor: 'rgba(255, 255, 255, 0.5)',
              fontSize: { xs: '0.7rem', sm: '0.8rem' },
              py: 0.5,
              display: { xs: 'none', sm: 'flex' }
            }}
          >
            Session Info
          </Button>

          {hasData && dataSummary && (
            <Stack direction="row" spacing={1} sx={{ mr: 2, display: { xs: 'none', md: 'flex' } }}>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                disableElevation
                sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  py: 0.5,
                  minWidth: 0,
                  color: 'white',
                  border: '1px solid',
                  borderColor: 'rgba(255, 255, 255, 0.5)'
                }}
              >
                {dataSummary.total_records} Records
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                disableElevation
                sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  py: 0.5,
                  minWidth: 0,
                  color: 'white',
                  border: '1px solid',
                  borderColor: 'rgba(255, 255, 255, 0.5)'
                }}
              >
                {dataSummary.unique_hotels} Hotels
              </Button>
            </Stack>
          )}

          <IconButton
            color="inherit"
            aria-label="Open settings"
            onClick={onConfigOpen}
            sx={{ mr: 1 }}
          >
            <SettingsIcon />
          </IconButton>
          
          {/* User account menu */}
          <Box>
            <IconButton
              size="large"
              aria-label="account menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleUserMenuOpen}
              color="inherit"
            >
              <AccountCircleIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={userMenuAnchor}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(userMenuAnchor)}
              onClose={handleUserMenuClose}
            >
              <MenuItem disabled>
                <Typography variant="body2">Signed in as <b>{username}</b></Typography>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Container 
        maxWidth="lg" 
        sx={{ 
          flex: 1, 
          py: 4, 
          display: 'flex', 
          flexDirection: 'column'
        }}
      >
        <Box 
          sx={{ 
            bgcolor: 'background.paper', 
            borderRadius: 2, 
            boxShadow: 1, 
            p: { xs: 2, sm: 3 }, 
            flex: 1,
          }}
        >
          {children}
        </Box>
      </Container>

      <Box 
        component="footer" 
        sx={{ 
          py: 2, 
          textAlign: 'center',
          backgroundColor: theme.palette.grey[100],
          borderTop: `1px solid ${theme.palette.divider}`,
          mt: 'auto'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          ABTSolute Concierge
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          © {new Date().getFullYear()} ABTSolute. All rights reserved.
        </Typography>
        
        {/* Mobile session button (only visible on xs screens) */}
        <Box sx={{ mt: 1.5, width: '100%', display: { xs: 'flex', sm: 'none' }, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setSessionDialogOpen(true)}
            size="small"
            sx={{ 
              fontSize: '0.7rem',
              py: 0.5
            }}
          >
            Session Info
          </Button>
        </Box>

        {/* Session Information Dialog */}
        <Dialog
          open={sessionDialogOpen}
          onClose={() => setSessionDialogOpen(false)}
          aria-labelledby="session-dialog-title"
        >
          <DialogTitle id="session-dialog-title">Session Information</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Session ID:</strong> {clientId}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Created:</strong> {new Date(sessionCreationDate).toLocaleString()}
            </Typography>
            <Typography variant="body2">
              This session ID is used to isolate your database instance from other users.
              Each uploaded dataset is linked to your specific session.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSessionDialogOpen(false)} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default AppLayout;
